/**
 * ProjectMindmapView - Hiển thị dự án dạng sơ đồ mindmap
 * Sử dụng ReactFlow để render tree diagram
 * Root node = Tên dự án, chỉ hiển thị branches có dữ liệu
 * Layout tự động tính toán để tránh chồng chéo
 */
import { useCallback, useMemo } from 'react'
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
} from 'reactflow'
import 'reactflow/dist/style.css'
import {
  HiOutlineInformationCircle,
  HiOutlineLocationMarker,
  HiOutlineTemplate,
  HiOutlineSparkles,
  HiOutlineCurrencyDollar,
  HiOutlineChartBar,
  HiOutlineOfficeBuilding,
} from 'react-icons/hi'

// Branch configuration
const branchConfig = {
  overview: {
    label: 'Tổng quan',
    icon: HiOutlineInformationCircle,
    color: '#3B82F6', // blue-500
    bgColor: '#EFF6FF', // blue-50
  },
  location: {
    label: 'Vị trí',
    icon: HiOutlineLocationMarker,
    color: '#22C55E', // green-500
    bgColor: '#F0FDF4', // green-50
  },
  design: {
    label: 'Thiết kế',
    icon: HiOutlineTemplate,
    color: '#A855F7', // purple-500
    bgColor: '#FAF5FF', // purple-50
  },
  amenities: {
    label: 'Tiện ích',
    icon: HiOutlineSparkles,
    color: '#F59E0B', // amber-500
    bgColor: '#FFFBEB', // amber-50
  },
  pricing: {
    label: 'Giá & CSBH',
    icon: HiOutlineCurrencyDollar,
    color: '#EF4444', // red-500
    bgColor: '#FEF2F2', // red-50
  },
  progress: {
    label: 'Tiến độ',
    icon: HiOutlineChartBar,
    color: '#06B6D4', // cyan-500
    bgColor: '#ECFEFF', // cyan-50
  },
}

// Custom node styles
const nodeStyles = {
  root: {
    padding: '16px 24px',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, #0EA5E9 0%, #3B82F6 100%)',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '16px',
    boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
    border: 'none',
    minWidth: '180px',
    textAlign: 'center',
  },
  branch: (color, bgColor) => ({
    padding: '10px 14px',
    borderRadius: '10px',
    background: bgColor,
    border: `2px solid ${color}`,
    color: color,
    fontWeight: '600',
    fontSize: '13px',
    minWidth: '120px',
    textAlign: 'center',
  }),
  leaf: () => ({
    padding: '8px 12px',
    borderRadius: '8px',
    background: 'white',
    border: '1px solid #E5E7EB',
    color: '#374151',
    fontSize: '12px',
    minWidth: '180px',
    maxWidth: '280px',
    textAlign: 'left',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  }),
}

/**
 * Generate nodes and edges from project data
 * Only show branches that have items
 * Dynamic layout based on number of active branches
 */
function generateNodesAndEdges(project) {
  const nodes = []
  const edges = []
  const branches = project.branches || {}

  // Filter to only branches with content
  const branchKeys = ['overview', 'location', 'design', 'amenities', 'pricing', 'progress']
  const activeBranches = branchKeys.filter(key => {
    const items = branches[key]?.items || []
    // Filter out __map__ and empty items
    const realItems = items.filter(item => !item.startsWith('__'))
    return realItems.length > 0
  })

  if (activeBranches.length === 0) {
    // No data - just show root
    nodes.push({
      id: 'root',
      type: 'default',
      position: { x: 300, y: 100 },
      data: {
        label: (
          <div className="flex items-center gap-2 justify-center">
            <HiOutlineOfficeBuilding className="w-5 h-5" />
            <span>{project.name}</span>
          </div>
        ),
      },
      style: nodeStyles.root,
    })
    return { nodes, edges }
  }

  // Calculate layout based on number of branches
  const BRANCH_WIDTH = 280 // Width per branch column (increased for full text)
  const BRANCH_SPACING = 40 // Gap between branches
  const totalWidth = activeBranches.length * BRANCH_WIDTH + (activeBranches.length - 1) * BRANCH_SPACING
  const startX = 50

  // Root node - centered above branches
  const rootX = startX + totalWidth / 2 - 90
  nodes.push({
    id: 'root',
    type: 'default',
    position: { x: rootX, y: 30 },
    data: {
      label: (
        <div className="flex items-center gap-2 justify-center">
          <HiOutlineOfficeBuilding className="w-5 h-5" />
          <span className="truncate max-w-[150px]">{project.name}</span>
        </div>
      ),
    },
    style: nodeStyles.root,
  })

  // Position branches horizontally
  activeBranches.forEach((key, index) => {
    const config = branchConfig[key]
    const branch = branches[key]
    const items = (branch?.items || []).filter(item => !item.startsWith('__'))
    const Icon = config.icon

    // Branch X position
    const branchX = startX + index * (BRANCH_WIDTH + BRANCH_SPACING)
    const branchY = 150

    // Branch node
    const branchId = `branch-${key}`
    nodes.push({
      id: branchId,
      type: 'default',
      position: { x: branchX, y: branchY },
      data: {
        label: (
          <div className="flex items-center gap-1.5 justify-center">
            <Icon className="w-4 h-4 flex-shrink-0" />
            <span>{config.label}</span>
          </div>
        ),
      },
      style: nodeStyles.branch(config.color, config.bgColor),
    })

    // Edge from root to branch
    edges.push({
      id: `edge-root-${key}`,
      source: 'root',
      target: branchId,
      type: 'smoothstep',
      style: { stroke: config.color, strokeWidth: 2 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: config.color,
        width: 15,
        height: 15,
      },
    })

    // Leaf nodes - limit to 4 items max, vertically stacked
    const MAX_ITEMS = 4
    const LEAF_HEIGHT = 45 // Height per leaf (increased for full text)
    const LEAF_SPACING = 10
    const displayItems = items.slice(0, MAX_ITEMS)

    displayItems.forEach((item, itemIndex) => {
      const leafId = `leaf-${key}-${itemIndex}`
      const leafY = branchY + 70 + itemIndex * (LEAF_HEIGHT + LEAF_SPACING)

      nodes.push({
        id: leafId,
        type: 'default',
        position: { x: branchX - 30, y: leafY },
        data: {
          label: (
            <div>
              {item}
            </div>
          ),
        },
        style: nodeStyles.leaf(config.color),
      })

      edges.push({
        id: `edge-${key}-${itemIndex}`,
        source: branchId,
        target: leafId,
        type: 'smoothstep',
        style: { stroke: '#D1D5DB', strokeWidth: 1 },
      })
    })

    // Show "more" indicator if there are more items
    if (items.length > MAX_ITEMS) {
      const moreId = `more-${key}`
      const moreY = branchY + 70 + MAX_ITEMS * (LEAF_HEIGHT + LEAF_SPACING)

      nodes.push({
        id: moreId,
        type: 'default',
        position: { x: branchX - 30, y: moreY },
        data: {
          label: `+${items.length - MAX_ITEMS} mục khác`,
        },
        style: {
          ...nodeStyles.leaf(config.color),
          fontStyle: 'italic',
          color: '#9CA3AF',
          background: '#F9FAFB',
        },
      })

      edges.push({
        id: `edge-more-${key}`,
        source: branchId,
        target: moreId,
        type: 'smoothstep',
        style: { stroke: '#E5E7EB', strokeWidth: 1, strokeDasharray: '4' },
      })
    }
  })

  return { nodes, edges }
}

/**
 * ProjectMindmapView Component
 */
export default function ProjectMindmapView({ project }) {
  const branches = project.branches || {}

  // Get active branches for legend
  const activeBranchKeys = useMemo(() => {
    const branchKeys = ['overview', 'location', 'design', 'amenities', 'pricing', 'progress']
    return branchKeys.filter(key => {
      const items = branches[key]?.items || []
      const realItems = items.filter(item => !item.startsWith('__'))
      return realItems.length > 0
    })
  }, [branches])

  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => generateNodesAndEdges(project),
    [project]
  )

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)

  // Fit view on initial render
  const onInit = useCallback((reactFlowInstance) => {
    reactFlowInstance.fitView({ padding: 0.3 })
  }, [])

  // Empty state
  if (activeBranchKeys.length === 0) {
    return (
      <div className="w-full h-[400px] bg-gray-50 rounded-xl border flex items-center justify-center">
        <div className="text-center text-gray-500">
          <HiOutlineOfficeBuilding className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="font-medium">{project.name}</p>
          <p className="text-sm mt-1">Chưa có thông tin chi tiết</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-[500px] md:h-[600px] bg-gray-50 rounded-xl border overflow-hidden relative">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onInit={onInit}
        fitView
        attributionPosition="bottom-left"
        proOptions={{ hideAttribution: true }}
        minZoom={0.4}
        maxZoom={1.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
      >
        <Controls
          showInteractive={false}
          className="!bg-white !border !rounded-lg !shadow-md"
        />
        <Background color="#E5E7EB" gap={20} />
      </ReactFlow>

      {/* Legend - only show active branches */}
      <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur rounded-lg border shadow-sm px-3 py-2">
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {activeBranchKeys.map((key) => {
            const config = branchConfig[key]
            return (
              <div key={key} className="flex items-center gap-1.5">
                <div
                  className="w-2.5 h-2.5 rounded-sm"
                  style={{ backgroundColor: config.color }}
                />
                <span className="text-xs text-gray-600">{config.label}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
